import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { ArrowLeftRight, CircleDashed } from 'lucide-react';
import { formatDate } from '@/utils/dateUtils';

interface ShiftRequestPayloadProps {
  payload: Record<string, unknown>;
  i18n: {
    _: (d: MessageDescriptor | { id: string; message: string; values?: Record<string, unknown> }) => string;
  };
}

/** Local copy of WorkflowPayloadDetail's FieldRow (a detail panel, not a table). */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function readString(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Renders a `shift_request` item (#2040): a claim on an open shift, or a swap a colleague
 * accepted. Approve gives the shift to the employee (a claim as confirmed, a swap as
 * published, so the colleague confirms it); Reject leaves it as it was. Both notify the
 * employees involved.
 */
export function ShiftRequestPayload({ payload, i18n }: ShiftRequestPayloadProps) {
  const swap = readString(payload, 'kind') === 'swap';
  const date = readString(payload, 'shift_date');
  const start = readString(payload, 'start_time');
  const end = readString(payload, 'end_time');
  const requester = readString(payload, 'requester_name');
  const counterpart = readString(payload, 'counterpart_name');
  const groups = Array.isArray(payload.group_names)
    ? (payload.group_names as unknown[]).filter((g): g is string => typeof g === 'string')
    : [];
  const Icon = swap ? ArrowLeftRight : CircleDashed;

  return (
    <div className="space-y-4" data-testid="shift-request-payload">
      <p className="flex items-center gap-2 text-sm font-medium">
        <Icon className="h-4 w-4" aria-hidden="true" />
        {swap
          ? i18n._(msg({ id: 'shiftRequests.payload.swapTitle', message: 'Shift swap' }))
          : i18n._(msg({ id: 'shiftRequests.payload.claimTitle', message: 'Request for an open shift' }))}
      </p>
      {date && (
        <FieldRow label={i18n._(msg({ id: 'shiftRequests.payload.shift', message: 'Shift' }))}>
          <p className="text-sm">
            {formatDate(date)}
            {start && end ? `, ${start}–${end}` : ''}
          </p>
        </FieldRow>
      )}
      {groups.length > 0 && (
        <FieldRow label={i18n._(msg({ id: 'shiftRequests.payload.groups', message: 'Groups' }))}>
          <p className="text-sm">{groups.join(', ')}</p>
        </FieldRow>
      )}
      {requester && (
        <FieldRow
          label={
            swap
              ? i18n._(msg({ id: 'shiftRequests.payload.holder', message: 'Gives the shift' }))
              : i18n._(msg({ id: 'shiftRequests.payload.claimant', message: 'Asks for the shift' }))
          }
        >
          <p className="text-sm">{requester}</p>
        </FieldRow>
      )}
      {swap && counterpart && (
        <FieldRow label={i18n._(msg({ id: 'shiftRequests.payload.taker', message: 'Takes the shift' }))}>
          <p className="text-sm">{counterpart}</p>
        </FieldRow>
      )}
      <p className="text-xs text-muted-foreground" data-testid="shift-request-effect">
        {swap
          ? i18n._(
              msg({
                id: 'shiftRequests.payload.swapEffect',
                message: 'Approve moves the shift to the colleague, who confirms it in the app. Both are notified.',
              })
            )
          : i18n._(
              msg({
                id: 'shiftRequests.payload.claimEffect',
                message: 'Approve gives the shift to this employee as confirmed. Everyone else who asked is told it is taken.',
              })
            )}
      </p>
    </div>
  );
}
